import { API_BASE_URL, API_ROOT } from "./apiConfig";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value || "");

const toReadableDate = (value) => {
  if (!value) {
    return "Unpublished";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unpublished";
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toAvatarInitials = (name) => {
  const parts = String(name || "BusiGrow Team")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "BG";
};

const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  if (isAbsoluteUrl(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE_URL}${value}`;
  }

  return `${API_BASE_URL}/${value}`;
};

export const normalizeBlog = (blog) => {
  const readTimeMinutes = Number(blog?.readTimeMinutes) || 5;
  const authorName = blog?.authorName || "BusiGrow Team";

  return {
    id: blog?.id,
    slug: blog?.slug,
    title: blog?.title || "Untitled",
    excerpt: blog?.excerpt || "",
    content: blog?.content || "",
    category: blog?.category || "General",
    image: resolveMediaUrl(blog?.coverImageUrl),
    coverImageUrl: resolveMediaUrl(blog?.coverImageUrl),
    author: authorName,
    authorName,
    authorAvatar: toAvatarInitials(authorName),
    readTime: `${readTimeMinutes} min read`,
    readTimeMinutes,
    date: toReadableDate(blog?.publishedAt || blog?.createdAt),
    featured: Boolean(blog?.isFeatured),
    isHero: Boolean(blog?.isHero),
    isPublished: Boolean(blog?.isPublished),
    publishedAt: blog?.publishedAt || null,
    createdAt: blog?.createdAt || null,
    tags: Array.isArray(blog?.tags) ? blog.tags : [],
  };
};

const toQueryString = (query) => {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    params.append(key, String(value));
  });

  return params.toString();
};

const parseApiResponse = async (response) => {
  const payload = await response.json();

  if (!response.ok || !payload?.success) {
    throw new Error(payload?.message || "Request failed");
  }

  return payload;
};

export const fetchPublishedBlogs = async (query = {}, options = {}) => {
  const queryString = toQueryString(query);
  const endpoint = queryString
    ? `${API_ROOT}/blog?${queryString}`
    : `${API_ROOT}/blog`;
  const response = await fetch(endpoint);
  const payload = await parseApiResponse(response);
  const items = Array.isArray(payload.data)
    ? payload.data.map(normalizeBlog)
    : [];

  if (options.returnMeta) {
    return {
      items,
      meta: payload.meta || {
        page: 1,
        limit: items.length,
        totalItems: items.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  return items;
};

export const fetchPublishedBlogBySlug = async (slug) => {
  const response = await fetch(`${API_ROOT}/blog/${slug}`);
  const payload = await parseApiResponse(response);
  return normalizeBlog(payload.data || {});
};

export const fetchAdminBlogs = async (token) => {
  const response = await fetch(`${API_ROOT}/admin/blogs`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const payload = await parseApiResponse(response);
  const data = payload.data;

  return Array.isArray(data) ? data.map(normalizeBlog) : [];
};

export const createAdminBlog = async (token, formData) => {
  const response = await fetch(`${API_ROOT}/admin/blogs`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const payload = await parseApiResponse(response);
  const data = payload.data;
  return normalizeBlog(data || {});
};

export const updateAdminBlog = async (token, blogId, formData) => {
  const response = await fetch(`${API_ROOT}/admin/blogs/${blogId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const payload = await parseApiResponse(response);
  const data = payload.data;
  return normalizeBlog(data || {});
};

export const deleteAdminBlog = async (token, blogId) => {
  const response = await fetch(`${API_ROOT}/admin/blogs/${blogId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  await parseApiResponse(response);
};

export { API_BASE_URL };
